/**
 * The Tafo logo as a drawing (ADR 0015, 0016): the four letters of the wordmark as outlines, each in its
 * own gradient, and the mark, which is the A above the O. Measured on the logo itself (4184 × 1344 pixels)
 * and redrawn; the unit is a pixel of that image. A letter also knows the lines it is written with.
 */
export type LetterName = "T" | "A" | "F" | "O";
export type MarkLetter = "A" | "O";
/** The lines the letters are written with: the letter and the place of the line in the writing of it. */
export type StrokeKey = "T0" | "T1" | "A0" | "A1" | "F0" | "F1" | "F2" | "O0";

export interface Point {
  x: number;
  y: number;
}

/** A line that writes a part of a letter. It is wider than that part: the outline of the letter cuts its edge. */
export interface Stroke {
  key: StrokeKey;
  /** The middle of the line, in the letter's own box. */
  path: string;
  width: number;
  length: number;
  /** Round where the line turns (the top of the A). */
  round?: true;
  /**
   * The line stays inside this region, so it does not show a neighbouring part before that part is written.
   * The border runs through the ink where two parts meet and leaves it at an angle: it never lies along an
   * edge of the letter, where two soft edges on top of each other would leave a hair of a line.
   */
  within?: string;
  /** Drawn in a slanted space (degrees, around this height), so its front is parallel to the cut end of the foot. */
  skew?: { degrees: number; around: number };
}

export interface Letter {
  /** The box of the letter in the wordmark. */
  x: number;
  y: number;
  width: number;
  height: number;
  /** In the letter's own box. */
  outline: string;
  /** Nine reds at equal steps along this line, from the lightest to the darkest. */
  gradient: { x1: number; y1: number; x2: number; y2: number; stops: readonly string[] };
  strokes: readonly Stroke[];
}

export const LETTER_NAMES: readonly LetterName[] = ["T", "A", "F", "O"];
export const MARK_LETTERS: readonly MarkLetter[] = ["A", "O"];

export const LETTERS: Record<LetterName, Letter> = {
  T: {
    x: 191.5,
    y: 191.5,
    width: 800,
    height: 960,
    outline:
      "M0 31.25A31.25 31.25 0 0 1 31.25 0L768.75 0A31.25 31.25 0 0 1 800 31.25L800 144.75A31.25 31.25 0 0 1 768.75 176L488 176L488 928.75A31.25 31.25 0 0 1 456.75 960L343.25 960A31.25 31.25 0 0 1 312 928.75L312 176L31.25 176A31.25 31.25 0 0 1 0 144.75Z",
    gradient: {
      x1: 45.3,
      y1: -15.4,
      x2: 650.7,
      y2: 830.2,
      stops: ["#ee0014", "#e70115", "#dd0916", "#d41017", "#cb1418", "#c21719", "#b81819", "#af1919", "#a6191a"],
    },
    strokes: [
      // the bar, which does not show the top of the stem below it
      {
        key: "T0",
        path: "M-30 88H830",
        width: 236,
        length: 860,
        within: "M-90 -90L890 -90L890 266L578 266L488 176L312 176L222 266L-90 266Z",
      },
      // the stem, from the middle of the bar
      { key: "T1", path: "M400 88V990", width: 236, length: 902 },
    ],
  },
  A: {
    x: 1124.09,
    y: 194.06,
    width: 934.86,
    height: 952.21,
    outline:
      "M371.73 51.66A89.44 89.44 0 0 1 534.36 53.32L929.85 940.96A8 8 0 0 1 922.54 952.21L761.05 952.21A8 8 0 0 1 753.77 947.54L454.74 294.46L214.2 780.87L519.52 780.87A8 8 0 0 1 526.72 785.39L601.73 940.73A8 8 0 0 1 594.52 952.21L85 952.21A86.83 86.83 0 0 1 6.43 828.43Z",
    gradient: {
      x1: 244.3,
      y1: 121.5,
      x2: 809.5,
      y2: 1019.7,
      stops: ["#e40816", "#d80d16", "#d01318", "#c71618", "#be1719", "#b4181a", "#ac191a", "#a21919", "#a11a1b"],
    },
    strokes: [
      // as the letter is written by hand: up the left leg and down the right one, from and to below the
      // baseline; the left leg ends on its own inner edge and does not show the foot between the legs
      {
        key: "A0",
        path: "M0.25 1032.21L455.36 88.78L881.52 1032.21",
        width: 242.09,
        length: 2082.68,
        round: true,
        within:
          "M-90 -90L1024.86 -90L1024.86 1042.21L723.92 1042.21L583.63 743.93L273.66 743.93L214.2 780.87L84.95 1042.21L-90 1042.21Z",
      },
      // then the foot, from under the left leg to its cut end
      {
        key: "A1",
        path: "M-90 866.54H595.9",
        width: 231.35,
        length: 685.9,
        skew: { degrees: 25.771, around: 866.54 },
      },
    ],
  },
  F: {
    x: 2191.5,
    y: 191.5,
    width: 728,
    height: 960,
    outline:
      "M0 31.25A31.25 31.25 0 0 1 31.25 0L696.75 0A31.25 31.25 0 0 1 728 31.25L728 144.75A31.25 31.25 0 0 1 696.75 176L176 176L176 392L584.75 392A31.25 31.25 0 0 1 616 423.25L616 536.75A31.25 31.25 0 0 1 584.75 568L176 568L176 928.75A31.25 31.25 0 0 1 144.75 960L31.25 960A31.25 31.25 0 0 1 0 928.75Z",
    gradient: {
      x1: -5.6,
      y1: 20.6,
      x2: 517.6,
      y2: 670.9,
      stops: ["#ee0014", "#e80015", "#e10616", "#d90d17", "#d11218", "#ca1518", "#c21719", "#ba1819", "#b31819"],
    },
    strokes: [
      // the stem, which does not show where the two bars leave it
      {
        key: "F0",
        path: "M88 -30V990",
        width: 236,
        length: 1020,
        within: "M-90 -90L176 -90L176 176L266 266L266 302L176 392L176 568L266 658L266 1050L-90 1050Z",
      },
      // the two bars, from the middle of the stem
      { key: "F1", path: "M88 88H758", width: 236, length: 670 },
      { key: "F2", path: "M88 480H646", width: 236, length: 558 },
    ],
  },
  O: {
    x: 3051.05,
    y: 215.98,
    width: 936.15,
    height: 936.15,
    outline:
      "M468.07 0A468.07 468.07 0 1 1 468.07 936.15A468.07 468.07 0 1 1 468.07 0ZM468.07 188.82A279.25 279.25 0 1 0 468.07 747.32A279.25 279.25 0 1 0 468.07 188.82Z",
    gradient: {
      x1: 225.5,
      y1: 74.4,
      x2: 711.5,
      y2: 863.2,
      stops: ["#e40816", "#d80d16", "#d01318", "#c71618", "#be1719", "#b4181a", "#ac191a", "#a11919", "#9c191a"],
    },
    strokes: [
      // clockwise from the top, and a little further than once round: no seam is left at the top
      {
        key: "O0",
        path: "M468.07 94.41A373.66 373.66 0 0 1 468.07 841.73A373.66 373.66 0 0 1 468.07 94.41A373.66 373.66 0 0 1 500.64 95.83",
        width: 248.82,
        length: 2380.39,
      },
    ],
  },
};

/** Between the A and the O in the mark, as measured on the mark. */
const MARK_GAP = 117.5;

const WORD = {
  x: LETTERS.T.x,
  y: LETTERS.T.y,
  width: LETTERS.O.x + LETTERS.O.width - LETTERS.T.x,
  height: LETTERS.T.height,
};
const MARK = {
  width: LETTERS.O.width,
  height: LETTERS.A.height + MARK_GAP + LETTERS.O.height,
};

/**
 * Where the mark stands when the word has turned into it: in the middle of the word (the opening of the
 * till, the logo in the middle of a head) or at its start (the logo at the left of a bar, with something
 * next to it).
 */
export type LogoLayout = "middle" | "start";

/**
 * The stage of the animation: wide enough for the word and high enough for the mark. In the middle there
 * is room around both for the turn; at the start the stage is no larger than the word and the mark, so a
 * bar can place it to the pixel.
 */
export function stageOf(layout: LogoLayout): { width: number; height: number } {
  return layout === "middle" ? { width: 4400, height: 2500 } : { width: WORD.width, height: MARK.height };
}

/** The left of something as wide as `width` on the stage. */
function leftOf(layout: LogoLayout, width: number): number {
  return layout === "middle" ? (stageOf(layout).width - width) / 2 : 0;
}

/** Where a letter stands on the stage while the word is there. */
export function inWord(name: LetterName, layout: LogoLayout): Point {
  const letter = LETTERS[name];
  return {
    x: leftOf(layout, WORD.width) + letter.x - WORD.x,
    y: (stageOf(layout).height - WORD.height) / 2 + letter.y - WORD.y,
  };
}

/** Where the A and the O stand on the stage in the mark: the A above the O. */
export function inMark(name: MarkLetter, layout: LogoLayout): Point {
  const left = leftOf(layout, MARK.width);
  const top = (stageOf(layout).height - MARK.height) / 2;
  return name === "A"
    ? { x: left + (MARK.width - LETTERS.A.width) / 2, y: top }
    : { x: left, y: top + LETTERS.A.height + MARK_GAP };
}

/** The middle of the mark on the stage: the A and the O turn around it. */
export function middleOfMark(layout: LogoLayout): Point {
  return { x: leftOf(layout, MARK.width) + MARK.width / 2, y: stageOf(layout).height / 2 };
}

/**
 * How wide the mark and the word are, in heights of the mark: a bar gives the logo at its start this room
 * (the mark stays, the word is written over what stands next to it).
 */
export const MARK_WIDE = MARK.width / MARK.height;
export const WORD_WIDE = WORD.width / MARK.height;

/** A line is drawn as one dash, a little longer than the line itself: nothing is left over. */
export function dashOf(stroke: Stroke): number {
  return Math.ceil(stroke.length) + 3;
}
